import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react'
import type { AttendantMetric } from '@/types/attendance-metrics'

interface AttendantMetricsTableProps {
  data: AttendantMetric[]
  sortBy?: keyof AttendantMetric
  sortOrder?: 'asc' | 'desc'
  onSort?: (column: keyof AttendantMetric) => void
  isLoading?: boolean
}

function formatTime(seconds: number | null): string {
  if (seconds === null) return 'N/A'
  if (seconds < 60) return `${seconds.toFixed(0)}s`
  return `${(seconds / 60).toFixed(1)}min`
}

export function AttendantMetricsTable({
  data,
  sortBy,
  sortOrder,
  onSort,
  isLoading,
}: AttendantMetricsTableProps) {
  const SortIcon = ({ column }: { column: keyof AttendantMetric }) => {
    if (sortBy !== column) return <ArrowUpDown className="h-3 w-3 ml-1" />
    return sortOrder === 'asc' ? (
      <ArrowUp className="h-3 w-3 ml-1" />
    ) : (
      <ArrowDown className="h-3 w-3 ml-1" />
    )
  }

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    )
  }

  return (
    <div className="border rounded-md">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 -ml-3"
                onClick={() => onSort?.('attendantName')}
              >
                Nome
                <SortIcon column="attendantName" />
              </Button>
            </TableHead>
            <TableHead>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 -ml-3"
                onClick={() => onSort?.('frtAvg')}
              >
                FRT
                <SortIcon column="frtAvg" />
              </Button>
            </TableHead>
            <TableHead>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 -ml-3"
                onClick={() => onSort?.('tmrAvg')}
              >
                TMR
                <SortIcon column="tmrAvg" />
              </Button>
            </TableHead>
            <TableHead>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 -ml-3"
                onClick={() => onSort?.('totalMessages')}
              >
                Mensagens
                <SortIcon column="totalMessages" />
              </Button>
            </TableHead>
            <TableHead>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 -ml-3"
                onClick={() => onSort?.('totalResponses')}
              >
                Respostas
                <SortIcon column="totalResponses" />
              </Button>
            </TableHead>
            <TableHead>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 -ml-3"
                onClick={() => onSort?.('totalTickets')}
              >
                Tickets
                <SortIcon column="totalTickets" />
              </Button>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((attendant) => (
            <TableRow key={attendant.attendantOdataId}>
              <TableCell className="font-medium">{attendant.attendantName}</TableCell>
              <TableCell>{formatTime(attendant.frtAvg)}</TableCell>
              <TableCell>{formatTime(attendant.tmrAvg)}</TableCell>
              <TableCell>
                <Badge variant="outline">{attendant.totalMessages}</Badge>
              </TableCell>
              <TableCell>
                <Badge variant="outline">{attendant.totalResponses}</Badge>
              </TableCell>
              <TableCell>
                <Badge variant="outline">{attendant.totalTickets}</Badge>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
